import React, {useCallback} from 'react';
import clsx from 'clsx';
import {RequiredKeys} from '@netx/core/typings';

import {
	ListItem,
	type ListItemProps,
	ListItemAvatar,
	ListItemText,
} from '@mui/material';
import {createStyles, withStyles, type WithStyles} from '@mui/styles';
import type {Theme} from '@netx/core-components/Theme';

import ColoredAvatar from '@netx/core-components/ColoredAvatar';

import {PERMISSION_TYPE_LABEL} from '../../locales';
import {AttributePermission} from '../../types';
import PermissionItemMenu, {
	type PermissionItemMenuProps,
} from '../ListItemMenu';

export type {PermissionItemMenuProps} from '../ListItemMenu';
export type MinimumPermissionItemMenuProps = Pick<
	PermissionItemMenuProps,
	RequiredKeys<PermissionItemMenuProps>
>;

export type PermissionListItemProps<
	MenuProps extends MinimumPermissionItemMenuProps = PermissionItemMenuProps,
> = Partial<Omit<ListItemProps<'li'>, 'button' | 'onChange'>> & {
	onChange(permission: AttributePermission, newPerm: 'read' | 'write'): void;
	onRemove(permission: AttributePermission): void;
	permission: AttributePermission;
	PermissionItemMenuComponent?: React.ComponentType<MenuProps>;
	PermissionItemMenuProps?: Partial<MenuProps>;
};

const styles = ({palette, spacing}: Theme) =>
	createStyles({
		root: {
			borderRadius: '28px 0 0 28px',
			outline: 'none !important',
			paddingLeft: 10,
			marginLeft: spacing(2),
			width: `calc(100% - ${spacing(2)}) !important`,
			'&:hover, &:focus': {
				backgroundColor: palette.action.hover,
			},
		},
		listItemAction: {
			flex: '0 0 auto',
			paddingLeft: spacing(2),
		},
		selected: {
			backgroundColor: palette.action.hover,
		},
	});

const PermissionListItem = <
	MenuProps extends MinimumPermissionItemMenuProps = PermissionItemMenuProps,
>({
	classes,
	permission,
	onChange,
	onRemove,
	selected,
	PermissionItemMenuProps,
	PermissionItemMenuComponent = PermissionItemMenu,
	...props
}: PermissionListItemProps<MenuProps> & WithStyles<typeof styles>) => {
	const handleChange = useCallback(
		(perm: 'read' | 'write') => onChange(permission, perm),
		[permission, onChange],
	);

	const handleRemove = useCallback(
		() => onRemove(permission),
		[permission, onRemove],
	);

	return (
		<ListItem
			aria-label={`${permission.groupName}: ${
				PERMISSION_TYPE_LABEL[permission.write ? 'w' : 'r']
			}`}
			className={clsx(classes.root, {
				[classes.selected]: selected,
			})}
			component="li"
			id={`permission-${permission.groupId.toString()}`}
			{...props}
		>
			<ListItemAvatar aria-hidden="true">
				<ColoredAvatar label={permission.groupName} />
			</ListItemAvatar>
			<ListItemText
				primaryTypographyProps={{
					noWrap: true,
				}}
				primary={permission.groupName}
				role="presentation"
			/>
			<div className={classes.listItemAction} role="presentation">
				<PermissionItemMenuComponent
					onChange={handleChange}
					onRemove={handleRemove}
					value={permission.write ? 'write' : 'read'}
					{...(PermissionItemMenuProps as any)}
				/>
			</div>
		</ListItem>
	);
};

export default withStyles(styles)(PermissionListItem) as <
	MenuProps extends MinimumPermissionItemMenuProps = PermissionItemMenuProps,
>(
	props: Omit<PermissionListItemProps<MenuProps>, 'classes'>,
) => JSX.Element;
